import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import getLabelByValue from "sr/helpers/functions/getLabelByValue";
import { fetchprovince } from "sr/redux/action/master/provinceAction";
import { AppDispatch, RootState } from "sr/redux/store";
import { getRegion } from "sr/utils/api/city/getGeofenceCity";

type Props = {
  regionIds?: string[];
  maxChipWidthClassName?: string;
};

export const RegionChips = ({
  regionIds = [],
  maxChipWidthClassName = "max-w-[260px]",
}: Props) => {
  const ids = useMemo(
    () => (Array.isArray(regionIds) ? regionIds.map(String).filter(Boolean) : []),
    [regionIds],
  );

  const provinceData = useSelector((s: RootState) => s.province?.data);
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    if (!provinceData) dispatch(fetchprovince());
  }, [provinceData, dispatch]);

  const [regionMetaById, setRegionMetaById] = useState<
    Record<string, { city?: string; state?: any }>
  >({});

  useEffect(() => {
    if (!ids.length) return;
    let cancelled = false;
    (async () => {
      const lookups = await Promise.all(
        ids.map(async (id) => {
          try {
            const qp = new URLSearchParams();
            qp.append("id", id);
            const res = await getRegion(qp.toString());
            if (!res?.success) return null;
            const city = Array.isArray(res?.data) ? res?.data?.[0] : res?.data;
            if (!city) return null;
            return { id: String(city.id), city: city.city, state: city.state };
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      const next: Record<string, { city?: string; state?: any }> = {};
      lookups.filter(Boolean).forEach((item: any) => {
        next[String(item.id)] = { city: item.city, state: item.state };
      });
      setRegionMetaById((prev) => ({ ...(prev || {}), ...next }));
    })();
    return () => {
      cancelled = true;
    };
  }, [ids]);

  const normalizeLabelToString = (raw: any): string => {
    if (!raw) return "";
    if (typeof raw === "string") return raw;
    if (typeof raw === "number") return String(raw);
    const maybeId = raw?.props?.id;
    if (typeof maybeId === "string") return String(maybeId);
    const maybeLabel = raw?.label;
    if (typeof maybeLabel === "string") return maybeLabel;
    return "";
  };

  const normalizeStateLabel = (rawState: any): string => {
    if (!rawState) return "";
    if (typeof rawState === "string") {
      const fromMap: any = getLabelByValue(rawState, provinceData || []);
      const mapped = normalizeLabelToString(fromMap);
      return mapped && mapped !== "Unknown" ? mapped : rawState;
    }
    if (typeof rawState === "object") {
      const value = rawState?.value;
      const label = rawState?.label;
      if (typeof value === "string") {
        const fromMap: any = getLabelByValue(value, provinceData || []);
        const mapped = normalizeLabelToString(fromMap);
        return mapped && mapped !== "Unknown" ? mapped : value;
      }
      if (typeof label === "string") return label;
    }
    return "";
  };

  const chips = useMemo(() => {
    return ids
      .map((id) => {
        const meta = regionMetaById?.[String(id)];
        if (!meta?.city) return null;
        const city = String(meta.city || "").trim();
        const state = normalizeStateLabel(meta.state).trim();
        const label = city && state ? `${city} (${state})` : city || state || "";
        if (!label) return null;
        return { id: String(id), label };
      })
      .filter(Boolean) as Array<{ id: string; label: string }>;
  }, [ids, regionMetaById, provinceData]);

  const ready = useMemo(() => {
    if (!ids.length) return true;
    return ids.every((id) => Boolean(regionMetaById?.[String(id)]?.city));
  }, [ids, regionMetaById]);

  if (!ids.length) return <span className="text-sm text-gray-500">-</span>;

  if (!ready) {
    return (
      <span className="text-sm text-gray-500">
        <FormattedMessage id="LOADING" defaultMessage="Loading..." />
      </span>
    );
  }

  return (
    <>
      {chips.map((c) => (
        <span
          key={`region-chip-${c.id}`}
          className="inline-flex items-center rounded-full bg-neutral-100 border border-neutral-200 px-3 py-1 text-sm text-gray-900"
          title={c.label}
        >
          <span className={`truncate ${maxChipWidthClassName}`}>{c.label}</span>
        </span>
      ))}
    </>
  );
};

